import * as Sentry from "@sentry/nextjs";
import { useTranslation } from "next-i18next";
import { OpenSeaAsset } from "opensea-js/lib/types";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useSnackbar } from "@/features/common/hooks/snackbar/useSnackbar";
import { useNftContext } from "@/features/nft/hooks/useNft";
import { useUserContext } from "@/features/user/contexts/useUserContext";
import { getPublicConfig } from "@/helpers/getPublicConfig";

export const useOpenSeaAsset = (tokenId: string) => {
  const {
    service: { openSea },
  } = getPublicConfig();
  const { user } = useUserContext();
  const {
    seaport,
    activeAccount,
    connectOrOnBoard,
    isSameWallet,
    isSameNetwork,
  } = useNftContext();
  const [refetch, setRefetch] = useState(false);
  const [asset, setAsset] = useState<OpenSeaAsset>(undefined);
  const isNftOwner = asset?.owner.address === user?.walletAccount;
  const listed = asset?.sellOrders.length > 0;

  const { t } = useTranslation("common");
  const { openSnackbar } = useSnackbar();

  const getAsset = useCallback(async () => {
    try {
      const asset = await seaport.api.getAsset({
        tokenAddress: openSea.contractAddress,
        tokenId,
      });
      setAsset(asset);
    } catch (error) {
      if (error.message.includes("404")) return;
    }
  }, [openSea, tokenId, seaport]);

  useEffect(() => {
    if (!seaport || !tokenId) return;
    getAsset();
  }, [seaport, tokenId, getAsset]);

  useEffect(() => {
    if (!refetch) return;
    const refetchAsset = async () => {
      await getAsset();
      setRefetch(false);
    };

    refetchAsset();
  }, [refetch, getAsset]);

  const buyNow = async (setLoadingCallback?: (state?: boolean) => void) => {
    if (isNftOwner) return;

    // check if user connected to wallet, under same network, using same wallet
    if (!activeAccount) return connectOrOnBoard();
    if (!isSameNetwork) {
      return openSnackbar({
        message: t("common:nft.networkMismatch"),
        severity: "error",
      });
    }
    if (!isSameWallet) {
      return openSnackbar({
        message: t("common:nft.pleaseSwitchWallet", {
          walletAccount: user.walletAccount,
        }),
        severity: "error",
      });
    }
    setLoadingCallback && setLoadingCallback(true);
    try {
      await seaport.fulfillOrder({
        order: asset.sellOrders[0],
        accountAddress: activeAccount,
      });
      setRefetch(true);
      setLoadingCallback && setLoadingCallback(false);
    } catch (e) {
      setLoadingCallback && setLoadingCallback(false);
      if (e.message.includes("insufficient funds"))
        return openSnackbar({
          message: t("common:nft.errors.insufficientFunds"),
          severity: "error",
        });
      if (e.message.includes("Failed to authorize transaction"))
        return openSnackbar({
          message: t("common:nft.errors.userDeclineTransaction"),
          severity: "error",
        });
      openSnackbar({
        message: t("common:nft.errors.transactionFail"),
        severity: "error",
      });
      Sentry.captureException(e, {
        tags: { seaport: "fulfillOrder" },
        extra: { tokenId },
      });
    }
    return;
  };

  const putOnSale = async (startAmount: number) => {
    if (!isNftOwner) return;

    // check if user connected to wallet, under same network, using same wallet
    if (!activeAccount) return connectOrOnBoard();
    if (!isSameNetwork) {
      return openSnackbar({
        message: t("common:nft.networkMismatch"),
        severity: "error",
      });
    }
    if (!isSameWallet) {
      return openSnackbar({
        message: t("common:nft.pleaseSwitchWallet", {
          walletAccount: user.walletAccount,
        }),
        severity: "error",
      });
    }
    try {
      await seaport.createSellOrder({
        asset: {
          tokenId,
          tokenAddress: openSea.contractAddress,
          //1155 need to  specify the schema name
        },
        accountAddress: activeAccount,
        startAmount: Number(startAmount),
      });
      setRefetch(true);
    } catch (e) {
      if (e.code === 4001) {
        openSnackbar({
          message: t("common:nft.errors.userDeclineTransaction"),
          severity: "error",
        });
        return;
      }
      openSnackbar({
        message: t("common:nft.errors.transactionFail"),
        severity: "error",
      });
      Sentry.captureException(e, {
        tags: { seaport: "putOnSale" },
        extra: { tokenId },
      });
    }
  };

  return {
    asset,
    buyNow,
    putOnSale,
    isNftOwner,
    listed,
  };
};
